import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { medicineApi, medicineProviderApi } from "apis";
import { useToast } from "contexts/ToastContext";
import {
  GetMedicinesResponseDto,
  GetMedicinesInPrescriptionOrder200Response,
  MedicineItemDto,
} from "libs/cellera-sdk";

export const useGetMedicines = (params: {
  page?: number;
  pageSize?: number;
  search?: string;
}) => {
  return useQuery<GetMedicinesResponseDto>({
    queryKey: ["medicines", params],
    queryFn: () => medicineApi.getMedicines(params),
    placeholderData: keepPreviousData,
  });
};

export const useGetMedicinesInPrescription = (prescriptionId?: string) => {
  return useQuery<GetMedicinesInPrescriptionOrder200Response>({
    queryKey: ["medicinesInPrescription", prescriptionId],
    queryFn: () => {
      if (!prescriptionId) {
        throw new Error("Prescription ID is required");
      }
      return medicineProviderApi.getMedicinesInPrescriptionOrder({
        prescriptionId: prescriptionId,
      });
    },
    placeholderData: keepPreviousData,
    enabled: !!prescriptionId,
  });
};

export const useAddMedicinesToPrescription = () => {
  const { showError } = useToast();
  return useMutation({
    mutationFn: (params: {
      prescriptionId: string;
      medicines: MedicineItemDto[];
    }) =>
      medicineProviderApi.addMedicinesToPrescriptionOrder({
        prescriptionId: params.prescriptionId,
        addMedicinesToPrescriptionOrderRequestDto: {
          medicines: params.medicines,
        },
      }),
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Add medicines to prescription failed. Please try again.";
      showError("Add Medicines Failed", errorMessage);
    },
  });
};

export const useRemoveMedicineFromPrescription = () => {
  const { showError } = useToast();
  return useMutation({
    mutationFn: (params: { prescriptionId: string; medicineId: string }) =>
      medicineProviderApi.removeMedicineFromPrescriptionOrder({
        prescriptionId: params.prescriptionId,
        medicineId: params.medicineId,
      }),
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Remove medicine from prescription failed. Please try again.";
      showError("Remove Medicine Failed", errorMessage);
    },
  });
};
